<script setup lang="ts">
import {
  assertIsValidSelectMatcher,
  type SelectOptionValueFilter,
} from '@/modules/Project/Filters/types'
import { TYPE_ICON } from '@/modules/Project/icons'

import type {
  MultiSelectProperty,
  SingleSelectProperty,
  UserSelectProperty,
} from '@/modules/Project/Properties/types'
import { exhaustiveGuard } from '@/shared/utils/typeAssertions'
import { computed } from 'vue'
import FilterItemContainer from './FilterItemContainer.vue'
import FilterBarItemMatcher, { type ID } from './FilterItemMatcher.vue'
import FilterBarSelectItemValuePicker from './FilterItemSelectValuePicker.vue'

const props = defineProps<{
  filter: SelectOptionValueFilter
  property: SingleSelectProperty | MultiSelectProperty | UserSelectProperty
}>()

const emit = defineEmits<{
  (e: 'delete'): void
  (e: 'update', filter: SelectOptionValueFilter): void
}>()

const onNewMatcher = (matchId: ID) => {
  const name = matchId === 'property_not_set' ? 'property_none_of' : matchId
  assertIsValidSelectMatcher(name)

  let newFilter: SelectOptionValueFilter
  switch (name) {
    case 'property_all_of': {
      newFilter = {
        subject: 'field_select_option_value',
        matcher: {
          name,
          property_id: props.filter.matcher.property_id,
          reference_support: props.filter.matcher.reference_support,
          values: props.filter.matcher.values,
        },
      }
      break
    }
    case 'property_any_of': {
      newFilter = {
        subject: 'field_select_option_value',
        matcher: {
          name,
          property_id: props.filter.matcher.property_id,
          reference_support: props.filter.matcher.reference_support,
          values: props.filter.matcher.values,
          case_sensitive: false,
        },
      }
      break
    }
    case 'property_none_of': {
      newFilter = {
        subject: 'field_select_option_value',
        matcher: {
          name,
          property_id: props.filter.matcher.property_id,
          reference_support: props.filter.matcher.reference_support,
          // 'property_not_set' is not a valid backend filter matcher, so we swap it out for
          // 'property_none_of' with all options selected.
          values:
            matchId === 'property_not_set'
              ? props.property.config.options.map((option) => option.value)
              : props.filter.matcher.values,
          case_sensitive: false,
        },
      }
      break
    }
    default: {
      return exhaustiveGuard(name, 'Unhandled select matcher name')
    }
  }

  emit('update', newFilter)
  return
}

const optionCount = computed<number | undefined>(() => props.property.config.options.length)
const hasSelectedIsNotSet = computed<boolean>(() => {
  return (
    'matcher' in props.filter &&
    props.filter.matcher.name === 'property_none_of' &&
    props.filter.matcher.values.length === optionCount.value
  )
})

const onNewValues = (values: string[]) => {
  const newFilter: SelectOptionValueFilter = {
    ...props.filter,
    matcher: { ...props.filter.matcher, values },
  }
  emit('update', newFilter)
}
</script>

<template>
  <FilterItemContainer
    :icon="TYPE_ICON[property.type]"
    :applied-to="property.name"
    @delete="$emit('delete')"
  >
    <FilterBarItemMatcher
      :filter="filter"
      :matcher-name="filter.matcher.name"
      :values="filter.matcher.values"
      :type="property.type"
      :option-count="optionCount"
      @match="onNewMatcher"
    />
    <FilterBarSelectItemValuePicker
      v-if="!hasSelectedIsNotSet"
      :filter="filter"
      :property="property"
      @delete="$emit('delete')"
      @values="(values: string[]) => onNewValues(values)"
    />
  </FilterItemContainer>
</template>
